import type { BlockStateTokensResponse } from "@cohesive/api-client";
import { orgAPI } from "./api";

/** Requests within this window share one call. */
const BATCH_MS = 50;
const MAX_BATCH = 256;
/** A token with less left than this is renewed before use. */
export const RENEW_UNDER_MS = 60_000;

export interface BlockStateToken {
  token: string;
  write: boolean;
  /** On the `performance.now()` clock, counted from receipt: the device
   *  clock may be off. */
  expiresAt: number;
}

/** The API refused a token for this file: no access, deleted, not HTML. */
export class BlockStateTokenRefused extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "BlockStateTokenRefused";
  }
}

type Waiter = {
  resolve: (t: BlockStateToken) => void;
  reject: (err: unknown) => void;
};

export class BlockStateTokens {
  private held = new Map<string, BlockStateToken>();
  private queued = new Map<string, Waiter[]>();
  private inFlight = new Map<string, Promise<BlockStateToken>>();
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private fetchTokens: (
      ids: string[],
    ) => Promise<BlockStateTokensResponse> = (ids) =>
      orgAPI.fileRecords.getBlockStateTokens(ids),
  ) {}

  /** The held token, if any, without asking for one. */
  peek(id: string): BlockStateToken | undefined {
    return this.held.get(id);
  }

  /** `renew` skips the held token, after the Worker refused it. */
  get(id: string, renew = false): Promise<BlockStateToken> {
    const held = this.held.get(id);
    if (!renew && held && held.expiresAt - performance.now() > RENEW_UNDER_MS) {
      return Promise.resolve(held);
    }
    if (renew) this.held.delete(id);
    const pending = this.inFlight.get(id);
    if (pending) return pending;
    const promise = new Promise<BlockStateToken>((resolve, reject) => {
      const waiters = this.queued.get(id) ?? [];
      waiters.push({ resolve, reject });
      this.queued.set(id, waiters);
    });
    this.inFlight.set(id, promise);
    this.timer ??= setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, BATCH_MS);
    return promise;
  }

  private async flush(): Promise<void> {
    const batch = [...this.queued.entries()];
    this.queued.clear();
    for (let i = 0; i < batch.length; i += MAX_BATCH) {
      void this.send(new Map(batch.slice(i, i + MAX_BATCH)));
    }
  }

  private async send(waiters: Map<string, Waiter[]>): Promise<void> {
    const settle = (id: string, fn: (w: Waiter) => void) => {
      this.inFlight.delete(id);
      for (const w of waiters.get(id) ?? []) fn(w);
      waiters.delete(id);
    };
    try {
      const { tokens } = await this.fetchTokens([...waiters.keys()]);
      const receivedAt = performance.now();
      for (const t of tokens) {
        if (!waiters.has(t.file_record_id)) continue;
        if (t.error) {
          const err =
            t.error.status >= 500
              ? new Error(t.error.message)
              : new BlockStateTokenRefused(t.error.status, t.error.message);
          settle(t.file_record_id, (w) => w.reject(err));
          continue;
        }
        const token: BlockStateToken = {
          token: t.token,
          write: t.write,
          expiresAt: receivedAt + t.expires_in * 1000,
        };
        this.held.set(t.file_record_id, token);
        settle(t.file_record_id, (w) => w.resolve(token));
      }
      const missing = new Error("no token in the response");
      for (const id of [...waiters.keys()])
        settle(id, (w) => w.reject(missing));
    } catch (err) {
      for (const id of [...waiters.keys()]) settle(id, (w) => w.reject(err));
    }
  }
}

export const blockStateTokens = new BlockStateTokens();
